/**
 * Native share-sheet user row.
 *
 * Rewritten off the Actionsheet atom family (SE-5547): the row is a plain
 * Pressable/Stack/Icon/Text composition visually equivalent to the former
 * ActionsheetItem (w-full row, 12px vertical padding, 16px gap, 40% opacity
 * when disabled). The AppleStyleSwipeableRow wrapper for the
 * swipe-to-remove-me gesture is preserved.
 * @module components/organisms/ShareActionItem
 */

import React, { PropsWithChildren } from "react";
import { Animated } from "react-native";
import { Swipeable } from "react-native-gesture-handler";

import { IShareActionProps } from "@/types/shareActionItemTypes";

import AppleStyleSwipeableRow from "@/components/molecules/AppleStyleSwipeableRow";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Props for the SwipeableRowWrapper component
 */
interface SwipeableRowWrapperProps extends PropsWithChildren {
  enableSwipe: boolean;
  onSwipeableOpenStartDrag?: (
    direction: "right" | "left",
    swipeable: Swipeable | null
  ) => void;
  renderRightActions?: (
    progress: Animated.AnimatedInterpolation<number>,
    drag: Animated.AnimatedInterpolation<number>
  ) => React.JSX.Element;
}

/**
 * Wrapper component that conditionally enables swipe gestures for share rows
 * @param root0 - Component props
 * @param root0.children - Child elements to be wrapped
 * @param root0.enableSwipe - Boolean flag to enable/disable swipe functionality
 * @param root0.onSwipeableOpenStartDrag - Callback function invoked when swipe gesture starts
 * @param root0.renderRightActions - Function to render custom right-side swipe actions
 */
const SwipeableRowWrapper = ({
  children,
  enableSwipe,
  onSwipeableOpenStartDrag,
  renderRightActions,
}: SwipeableRowWrapperProps) => {
  if (!enableSwipe) {
    return <>{children}</>;
  }
  return (
    <AppleStyleSwipeableRow
      overshootRight
      renderRightActions={renderRightActions}
      onSwipeableOpenStartDrag={onSwipeableOpenStartDrag}
    >
      {children}
    </AppleStyleSwipeableRow>
  );
};

/**
 * Native view component that renders a swipeable action item for sharing watchlists
 * @param root0 - Component props
 * @param root0.user - User data fragment for the watchlist member
 * @param root0.isMyWatchlist - Boolean flag indicating if the current user owns this watchlist
 * @param root0.onPress - Callback function invoked when the action item is pressed
 * @param root0.loading - Boolean flag indicating if an operation is in progress
 * @param root0.isMeInWatchlist - Boolean flag indicating if the current user is in this watchlist
 * @param root0.onSwipeableOpenStartDrag - Callback function invoked when swipe gesture starts
 * @param root0.renderRightActions - Function to render custom right-side swipe actions
 * @param root0.IconActionSheet - Icon component to display for the action
 */
const ShareActionItemView = ({
  user,
  isMyWatchlist,
  onPress,
  loading,
  isMeInWatchlist,
  onSwipeableOpenStartDrag,
  renderRightActions,
  IconActionSheet,
}: IShareActionProps) => (
  <SwipeableRowWrapper
    enableSwipe={!isMyWatchlist && isMeInWatchlist}
    onSwipeableOpenStartDrag={onSwipeableOpenStartDrag}
    renderRightActions={renderRightActions}
  >
    <Pressable
      onPress={isMyWatchlist ? onPress : undefined}
      disabled={!isMyWatchlist || loading}
      accessibilityRole="button"
      className={`w-full flex-row items-center gap-4 py-3 ${
        !isMyWatchlist || loading ? "opacity-40" : ""
      }`}
    >
      {isMyWatchlist &&
        (loading ? (
          <Spinner
            className="pt-1 text-status-info dark:text-content-primary"
            size="small"
          />
        ) : (
          IconActionSheet && (
            <Icon
              className="pt-1 text-status-info dark:text-content-primary"
              size="md"
              as={IconActionSheet}
            />
          )
        ))}
      <Stack className="shrink items-start">
        <Text variant="title" className="font-medium text-content-secondary">
          {
            user.cognitoUser?.Attributes?.find(
              attr => attr.Name === "given_name"
            )?.Value
          }
          {user.cognitoUser?.Attributes?.find(
            attr => attr.Name === "given_name"
          )?.Value &&
            user.cognitoUser?.Attributes?.find(
              attr => attr.Name === "family_name"
            )?.Value &&
            " "}
          {
            user.cognitoUser?.Attributes?.find(
              attr => attr.Name === "family_name"
            )?.Value
          }
        </Text>
        <Text variant="body" className="text-content-secondary">
          {user.title}
        </Text>
      </Stack>
    </Pressable>
  </SwipeableRowWrapper>
);

export default React.memo(ShareActionItemView);
